import { ApplicationRef, createComponent, inject, Injectable, Signal } from "@angular/core";
import { cloneObject } from "@globals/helper-functions";
import buffer from "@turf/buffer";
import { FeatureGroup } from "leaflet";
import { Observable, Subject } from "rxjs";
import { BiMapService } from "../bi-map.service";
import { BiMetersSettingComponent } from "../bi-meters-setting/bi-meters-setting.component";
import { BiMapMarker } from "../BiMapMarker";
import { DrawChangeEventData } from "../types-and-models/drawChangeEventData";

@Injectable()
export class BiMapBufferLinesHandlerService {
  /**
   * Maps an Id of a Polyline to its associated buffer Polygon
   */
  private polyLineIdsToBufferPolygonsMap: { [key: number]: L.Polygon } = {};
  private deletedLinesAndBufferPolygons: Array<{ line: L.Polyline; bufferPolygon: L.Polygon }> = [];

  /**
   * The current map instance that was set via the `setupBufferingForPolylines` method.
   */
  private map: L.Map;
  private mapDeleteModeEnabled: Signal<boolean>;
  /**
   * The current draw layer group instance that was set via the `setupBufferingForPolylines` method.
   */
  private drawLayerGroup: FeatureGroup;
  private defaultBufferMeters = 10;
  private readonly appRef = inject(ApplicationRef);
  private readonly mapService = inject(BiMapService);

  public onBufferLineEdited = new Subject<DrawChangeEventData>();
  public onBufferPolygonDeleted = new Subject<L.Polygon>();
  /**
   * Enabling the "Helicopter route" feature
   */
  public setupBufferingForPolylines(map: L.Map, deleteModeSignal: Signal<boolean>, drawLayerGroup: FeatureGroup) {
    this.map = map;
    this.drawLayerGroup = drawLayerGroup;
    this.mapDeleteModeEnabled = deleteModeSignal;
    // When Polyline has been created, create default buffer and open the meters setting popup
    this.map.on(L.Draw.Event.CREATED, (lineCreatedEvent: L.DrawEvents.Created) => {
      if (!(lineCreatedEvent.layer instanceof L.Polygon) && lineCreatedEvent.layer instanceof L.Polyline) this.createBufferForPolyline(lineCreatedEvent.layer, this.defaultBufferMeters, true);
    });

    this.map.on(L.Draw.Event.DELETED, () => (this.deletedLinesAndBufferPolygons = []));
  }

  /**
   * Call this when user cancels deletion of drawn shapes
   * @param drawLayerGroup the FeatureGroup that contains the drawn shapes
   * @param refreshMapLayers callback for refreshing the map layers after the cancel action
   * @param deleteModelStateSignal Signal tracking whether delete mode is active for the map
   */
  public onCancelDrawDelete(drawLayerGroup: FeatureGroup, refreshMapLayers: (layers: Array<L.Layer>) => Observable<any>) {
    // If delete mode has stopped and we still have deleted lines and buffers cached, it must be a "cancel" action. Therefore, revert the removal/deletion.
    if (this.deletedLinesAndBufferPolygons.length > 0) {
      this.deletedLinesAndBufferPolygons.forEach(lp => {
        drawLayerGroup.addLayer(lp.line);
        this.map.addLayer(lp.bufferPolygon);
        this.polyLineIdsToBufferPolygonsMap[L.stamp(lp.line)] = lp.bufferPolygon;
        this.setupBufferPolygonClickEvent(lp.bufferPolygon, lp.line);
      });
      refreshMapLayers(this.deletedLinesAndBufferPolygons.map(lp => lp.bufferPolygon)).subscribe();
      this.deletedLinesAndBufferPolygons = [];
    } else if (drawLayerGroup.getLayers().length === 0) {
      // Make sure all layers are removed - we could reach this point when user hits the "Delete all" option while buffer polygons are existing.
      this.map.eachLayer(layer => (layer instanceof L.Polygon ? this.map.removeLayer(layer) : undefined));
      this.polyLineIdsToBufferPolygonsMap = {};
    }
  }

  public onMapDrawEdited(e: L.DrawEvents.Edited, deleteMarkersInsidePolygon: (shapeId: number) => void) {
    const polyLines = e.layers.getLayers().filter(layer => this.mapService.isLayerPolyline(layer)) as Array<L.Polyline>;

    polyLines.forEach(polyLine => {
      const bufferPolygon = this.polyLineIdsToBufferPolygonsMap[L.stamp(polyLine)];
      if (bufferPolygon) {
        deleteMarkersInsidePolygon(L.stamp(bufferPolygon));
        this.deleteBufferForPolyline(polyLine, false);
        this.createBufferForPolyline(polyLine, this.defaultBufferMeters, polyLines.length === 1);
      }
    });
  }

  public getMarkersInsideBufferPolygon(markerLayerGroup: L.LayerGroup, bufferPolygonId: number): Array<BiMapMarker> {
    return (markerLayerGroup.getLayers() as Array<BiMapMarker>).filter(m => m.parentShapeId === bufferPolygonId);
  }

  public getBufferPolygons() {
    return cloneObject(Object.values(this.polyLineIdsToBufferPolygonsMap));
  }

  public getCurrentBufferPolygonsCount() {
    return Object.keys(this.polyLineIdsToBufferPolygonsMap).length;
  }

  public clearBufferPolygons() {
    this.polyLineIdsToBufferPolygonsMap = {};
  }

  /**
   * Generate a buffer polygon and associated line by a line's start- and end coordinates. Will also add this to the map.
   * @param startCoordinate Start coordinate of the line
   * @param endCoordinate End coordinate of the line
   * @param bufferWidth Width of the buffer created from the line
   */
  public generateAndAddBufferPolygonByStartAndEndCoordinate(startCoordinate: L.LatLng, endCoordinate: L.LatLng, bufferWidth?: number) {
    const polyline = new L.Polyline([startCoordinate, endCoordinate]);

    if (!bufferWidth) bufferWidth = this.defaultBufferMeters;
    polyline.bufferWidth = bufferWidth;

    const bufferGeoJson = buffer(polyline.toGeoJSON(), bufferWidth, { units: "meters" });
    const bufferPolygon = L.GeoJSON.geometryToLayer(bufferGeoJson) as L.Polygon;

    this.polyLineIdsToBufferPolygonsMap[L.stamp(polyline)] = bufferPolygon;

    bufferPolygon.addTo(this.map);

    if (!this.drawLayerGroup.hasLayer(polyline)) this.drawLayerGroup.addLayer(polyline);
  }

  private createBufferForPolyline(line: L.Polyline, lineWidth: number, openLineWidthSettingsPopup?: boolean): L.Polygon<any> | undefined {
    const lineId = L.stamp(line);
    const bufferGeoJson = buffer(line.toGeoJSON(), line.bufferWidth ?? this.defaultBufferMeters, { units: "meters" });

    this.polyLineIdsToBufferPolygonsMap[lineId] = (L.GeoJSON.geometryToLayer(bufferGeoJson) as L.Polygon).addTo(this.map);

    this.setupBufferPolygonClickEvent(this.polyLineIdsToBufferPolygonsMap[lineId], line);

    if (openLineWidthSettingsPopup) this.openLineWidthSettingsPopup(line, line.bufferWidth ?? lineWidth);

    return this.polyLineIdsToBufferPolygonsMap[lineId];
  }

  private setupBufferPolygonClickEvent(bufferPolygon: L.Polygon, polyLine: L.Polyline) {
    bufferPolygon.on("click", () => {
      if (this.mapDeleteModeEnabled()) {
        this.deleteBufferForPolyline(polyLine);
        this.drawLayerGroup.removeLayer(polyLine);
      }
    });
  }

  private deleteBufferForPolyline(line: L.Polyline, supportUndo = true) {
    const lineId = L.stamp(line);
    if (!this.polyLineIdsToBufferPolygonsMap[lineId]) return;
    // First store the deleted line and polygon so that we can support "undo" feature.
    if (supportUndo) this.deletedLinesAndBufferPolygons.push({ line: line, bufferPolygon: this.polyLineIdsToBufferPolygonsMap[lineId] });
    // Unsubscribe from the click event before removing the layer and its makers inside from the map
    this.polyLineIdsToBufferPolygonsMap[lineId].off("click");
    this.onBufferPolygonDeleted.next(this.polyLineIdsToBufferPolygonsMap[lineId]);
    delete this.polyLineIdsToBufferPolygonsMap[lineId];
  }

  private openLineWidthSettingsPopup(polyLine: L.Polyline, lineWidth: number) {
    const popup = new L.Popup(this.map.getCenter(), <L.PopupOptions>{ className: "meters-popup", closeOnClick: false, closeButton: false, keepInView: true });
    popup.openOn(this.map);

    // Generate the content for the popup by dynamically creating the BiMetersSettingComponent
    const metersSettingComponent = createComponent(BiMetersSettingComponent, { hostElement: document.getElementsByClassName("meters-popup").item(0), environmentInjector: this.appRef.injector });
    metersSettingComponent.setInput("defaultMeters", lineWidth);

    metersSettingComponent.instance.onMetersChanged.subscribe(meters => {
      polyLine.bufferWidth = meters;
      this.deleteBufferForPolyline(polyLine, false); // do not support "undo" as we're just removing the old and then adding the new buffer polygon
      this.createBufferForPolyline(polyLine, meters);
    });

    metersSettingComponent.instance.onOkClick.subscribe(() => {
      // Mark the layer buffered so that we do not include it when requesting server for addresses
      polyLine.isBuffered = true;
      popup.close();
      metersSettingComponent.destroy();
      this.onBufferLineEdited.next({ layers: [this.polyLineIdsToBufferPolygonsMap[L.stamp(polyLine)]], changeType: "create", isSingleMarker: false });
    });

    this.appRef.attachView(metersSettingComponent.hostView);
  }
}
